import { useRef, type CSSProperties } from 'react';
import { useInView } from './useInView';

type KineticTag = 'h1' | 'h2' | 'h3' | 'p' | 'span';

export interface KineticTextProps {
  /** Element to render (heading level stays semantic). */
  as?: KineticTag;
  /** Text split into words; the full string is kept for assistive tech. */
  text: string;
  /** Optional words appended after `text` with the accent colour (e.g. a place name). */
  accent?: string;
  /** Put the accent on its own line. */
  accentOnNewLine?: boolean;
  className?: string;
  accentClassName?: string;
  id?: string;
  /** Delay before the first word, in ms. */
  delay?: number;
  /** Delay between words, in ms. */
  stagger?: number;
  /** Hold the animation until this is true (e.g. hero photo loaded). */
  ready?: boolean;
  /** Visible fraction that triggers the reveal. */
  threshold?: number;
}

/**
 * Split into words. Each word becomes an inline-block, so the space before French
 * high punctuation (« : ; ! ? ») would turn into a line-break point; lone
 * punctuation is glued to the previous word with a no-break space instead.
 */
function words(value: string) {
  const out: string[] = [];
  for (const w of value.split(/\s+/).filter(Boolean)) {
    if (out.length && /^[:;!?»%]+$/.test(w)) out[out.length - 1] += ` ${w}`;
    else out.push(w);
  }
  return out;
}

/** Heading/paragraph whose words rise out of a mask with a soft blur, staggered, once it scrolls into view. */
export default function KineticText({
  as: Tag = 'h2',
  text,
  accent,
  accentOnNewLine = false,
  className = '',
  accentClassName = '',
  id,
  delay = 0,
  stagger = 55,
  ready = true,
  threshold = 0.3,
}: KineticTextProps) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { threshold });
  const play = inView && ready;

  const base = words(text);
  const extra = accent ? words(accent) : [];
  const full = accent ? `${text} ${accent}` : text;
  const style = { '--kt-delay': `${delay}ms`, '--kt-stagger': `${stagger}ms` } as CSSProperties;

  const renderWord = (word: string, index: number, accentWord: boolean) => (
    <span key={`${index}-${word}`} className={`kinetic-word ${accentWord ? `kinetic-accent ${accentClassName}` : ''}`}>
      <span style={{ '--i': index } as CSSProperties}>{word}</span>
    </span>
  );

  return (
    <Tag
      ref={ref as never}
      id={id}
      className={`kinetic ${play ? 'is-in' : ''} ${className}`}
      style={style}
    >
      <span className="visually-hidden">{full}</span>
      <span aria-hidden="true">
        {base.map((word, i) => (
          <span key={`b-${i}`}>
            {renderWord(word, i, false)}
            {i < base.length - 1 || extra.length ? ' ' : null}
          </span>
        ))}
        {extra.length > 0 && accentOnNewLine && <span className="kinetic-break" />}
        {extra.map((word, i) => (
          <span key={`a-${i}`}>
            {renderWord(word, base.length + i, true)}
            {i < extra.length - 1 ? ' ' : null}
          </span>
        ))}
      </span>
    </Tag>
  );
}
